import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Truck, User, LogIn, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { toast } from "sonner";

import API from "@/utils/api";
import Logo from "@/components/ui/Logo";

export default function DemoAccess() {
  const navigate = useNavigate();
  const { login, setUser } = useAuth();
  const [isLoading, setIsLoading] = useState(null);
  const [demoTokens, setDemoTokens] = useState({ admin: null, driver: null });

  // Fetch or create demo users on mount
  useEffect(() => {
    const setupDemoUsers = async () => {
      try {
        const response = await axios.post(`${API}/demo/setup`);
        setDemoTokens(response.data);
      } catch (error) {
        console.log("Demo setup not available, using direct navigation");
      }
    };
    setupDemoUsers();
  }, []);

  const handleDemoLogin = async (role) => {
    setIsLoading(role);

    try {
      // Create demo session via API
      const response = await axios.post(`${API}/demo/login`, { role }, { withCredentials: true });

      if (response.data.user) {
        setUser(response.data.user);
        toast.success(`¡Bienvenido ${role === 'admin' ? 'Administrador' : 'Repartidor'}!`);

        // Navigate based on role
        setTimeout(() => {
          if (role === 'admin') {
            navigate('/admin/pos', { replace: true, state: { user: response.data.user } });
          } else {
            navigate('/driver', { replace: true, state: { user: response.data.user } });
          }
        }, 100);
      }
    } catch (error) {
      console.error('Demo login error:', error);
      toast.error('Error al acceder. Intenta de nuevo.');
    } finally {
      setIsLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4 relative overflow-hidden">
      <div
        className="absolute inset-0 bg-repeat opacity-[0.25]"
        style={{
          backgroundImage: `url(/images/demo_background.png)`,
          backgroundSize: '400px',
        }}
      />
      {/* Gradiente oscuro */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60" />

      <div className="w-full max-w-md space-y-6 relative z-10">
        {/* Logo */}
        <div className="text-center">
          <div className="w-24 h-24 mx-auto mb-4 rounded-2xl overflow-hidden border-2 border-primary/30 flex items-center justify-center bg-white/5 backdrop-blur-md shadow-lg shadow-primary/20">
            <Logo size={40} variant="icon" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-white">
            Conexo POS
          </h1>
          <p className="text-gray-400 flex items-center justify-center gap-2 mt-2">
            <Flame className="w-4 h-4 text-orange-500" />
            Selecciona cómo quieres acceder
            <Flame className="w-4 h-4 text-orange-500" />
          </p>
        </div>

        {/* Role Cards */}
        <div className="space-y-3">
          <Card
            className="cursor-pointer bg-gray-900 border-gray-800 hover:border-yellow-500 transition-all hover:shadow-lg hover:shadow-yellow-500/20"
            onClick={() => navigate('/menu')}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center">
                  <User className="w-6 h-6 text-yellow-500" />
                </div>
                <div>
                  <CardTitle className="text-base text-white">CLIENTE</CardTitle>
                  <CardDescription className="text-gray-400">Ver menú y hacer pedidos</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card
            className="cursor-pointer bg-gray-900 border-gray-800 hover:border-orange-500 transition-all hover:shadow-lg hover:shadow-orange-500/20"
            onClick={() => handleDemoLogin('admin')}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <CardTitle className="text-base text-white">
                    ADMINISTRADOR
                    {isLoading === 'admin' && <span className="ml-2 text-sm text-yellow-500 animate-pulse">Cargando...</span>}
                  </CardTitle>
                  <CardDescription className="text-gray-400">Dashboard, menú y pedidos</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card
            className="cursor-pointer bg-gray-900 border-gray-800 hover:border-green-500 transition-all hover:shadow-lg hover:shadow-green-500/20"
            onClick={() => handleDemoLogin('driver')}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                  <Truck className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <CardTitle className="text-base text-white">
                    REPARTIDOR
                    {isLoading === 'driver' && <span className="ml-2 text-sm text-yellow-500 animate-pulse">Cargando...</span>}
                  </CardTitle>
                  <CardDescription className="text-gray-400">Gestionar entregas</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>
        </div>

        {/* Real Login */}
        <div className="pt-4 border-t border-gray-800 space-y-3">
          <Button
            variant="outline"
            className="w-full bg-transparent border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black"
            onClick={login}
            data-testid="demo-google-login"
          >
            <LogIn className="w-4 h-4 mr-2" />
            Iniciar sesión con Google
          </Button>

          <Button
            variant="ghost"
            className="w-full text-gray-500 hover:text-yellow-500"
            onClick={() => navigate('/login/driver')}
            data-testid="driver-login-link"
          >
            <Truck className="w-4 h-4 mr-2" />
            Acceso Repartidores (con contraseña)
          </Button>
        </div>
      </div>
    </div>
  );
}
